import eta from "@/templating/eta";

type TemplateEntry = { src: string; fn?: ReturnType<typeof eta.compile> };

type UpdateOptions = {
  allowMutationRefresh: boolean;
};

export class EtaTemplateCache {
  private _elementTemplates = new WeakMap<HTMLElement, TemplateEntry>();
  private _attrTemplates = new WeakMap<HTMLElement, Map<string, TemplateEntry>>();
  private _noEtaAttrs?: WeakSet<HTMLElement>;

  getElementEntry(el: HTMLElement): TemplateEntry | undefined {
    return this._elementTemplates.get(el);
  }

  hasTemplate(el: HTMLElement): boolean {
    const entry = this._elementTemplates.get(el);
    return Boolean(entry && entry.src && entry.src.includes("{{"));
  }

  getOrCreateAttrCache(el: HTMLElement): Map<string, TemplateEntry> {
    let cache = this._attrTemplates.get(el);
    if (!cache) {
      cache = new Map();
      this._attrTemplates.set(el, cache);
    }
    return cache;
  }

  hasNoEtaAttrs(el: HTMLElement): boolean {
    return this._noEtaAttrs?.has(el) ?? false;
  }

  markNoEtaAttrs(el: HTMLElement) {
    if (!this._noEtaAttrs) this._noEtaAttrs = new WeakSet<HTMLElement>();
    this._noEtaAttrs.add(el);
  }

  clearNoEtaAttrs(el: HTMLElement) {
    this._noEtaAttrs?.delete(el);
  }

  updateElementEntry(el: HTMLElement, currentSrc: string, { allowMutationRefresh }: UpdateOptions): TemplateEntry {
    let entry = this._elementTemplates.get(el);
    const hadTemplate = Boolean(entry && entry.src && entry.src.includes("{{"));

    if (!entry) {
      entry = { src: currentSrc };
      this._elementTemplates.set(el, entry);
      return entry;
    }

    if (entry.src !== currentSrc) {
      const isTemplateLike = currentSrc.includes("{{");
      // Accept SPA-authored changes (even without templates) only when mutation-triggered
      // and the element did not previously cache a template.
      if (isTemplateLike || (allowMutationRefresh && !hadTemplate)) {
        entry.src = currentSrc;
        delete entry.fn;
      }
    }

    return entry;
  }
}
